import React, { useEffect, useState } from "react";
import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { NATIVE_MINT } from "@solana/spl-token";
import { TxVersion, Percent } from "@raydium-io/raydium-sdk-v2";
import { fromBaseUnits, toBaseUnits } from "@oxminter/shared/amounts";
import { useApp } from "../state";
import { Addr, Field, Note, RequireToken, RequireWallet } from "../components/common";
import { getPoolInfoWithRetry, loadRaydium } from "../lib/raydium";
import { ataFor } from "../lib/token";
import { assertTxSucceeded, readTokenBalanceRaw, waitForPoolOpen } from "../lib/tx";
import { describeCountdown, describeOpenTime, isOpen } from "@oxminter/shared/schedule";

type Dir = "buy" | "sell";

/**
 * The pool's trading window, read from the pool account and compared against
 * the CLUSTER's clock rather than the browser's — the program uses the former,
 * and a machine a few minutes fast would otherwise show a pool as open while
 * every swap it built failed with NotApproved.
 */
function usePoolWindow() {
  const { connection, net, wallet, token } = useApp();
  const [openTime, setOpenTime] = useState<number | null>(null);
  const [clusterNow, setClusterNow] = useState<number | null>(null);

  useEffect(() => {
    if (!token?.poolId || !wallet) return;
    let live = true;
    const tick = async () => {
      try {
        const raydium = await loadRaydium(connection, net.cluster, wallet);
        const d = await raydium.cpmm.getRpcPoolInfo(token.poolId!, true);
        if (!live) return;
        setOpenTime(Number(d.openTime.toString()));
        const slot = await connection.getSlot("confirmed");
        const t = await connection.getBlockTime(slot);
        if (live && t) setClusterNow(t);
      } catch {
        /* leave the last known values; the panel says when it does not know */
      }
    };
    void tick();
    const id = setInterval(tick, 5000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [connection, net.cluster, wallet, token?.poolId]);

  const known = openTime !== null && clusterNow !== null;
  return {
    openTime,
    clusterNow,
    known,
    open: known ? isOpen(openTime as number, clusterNow as number) : true,
    countdown: known ? describeCountdown(openTime as number, clusterNow as number) : "",
  };
}

export default function Trade() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const window_ = usePoolWindow();
  const [dir, setDir] = useState<Dir>("buy");
  const [amount, setAmount] = useState("0.01");
  const [slippagePct, setSlippagePct] = useState("1");
  const [quote, setQuote] = useState<null | {
    inLabel: string;
    outLabel: string;
    outRaw: string;
    minOutRaw: string;
    priceImpact: string;
    outDecimals: number;
  }>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<null | {
    signature: string;
    quotedRaw: string;
    actualRaw: string;
    decimals: number;
    symbol: string;
  }>(null);

  const slippage = Number(slippagePct) / 100;

  async function doQuote() {
    if (!wallet || !token?.poolId) return;
    setBusy(true);
    setQuote(null);
    setResult(null);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, computePoolInfo } = await getPoolInfoWithRetry(() =>
        raydium.cpmm.getPoolInfoFromRpc(token.poolId!)
      );
      const wsol = NATIVE_MINT.toBase58();
      const inMint = dir === "buy" ? wsol : token.mint;
      const outMint = dir === "buy" ? token.mint : wsol;
      const inDecimals = dir === "buy" ? 9 : token.decimals;
      const outDecimals = dir === "buy" ? token.decimals : 9;

      if (![poolInfo.mintA.address, poolInfo.mintB.address].includes(wsol)) {
        throw new Error("this pool has no WSOL side; refusing to guess the direction");
      }

      const amountIn = toBaseUnits(amount, inDecimals);
      if (amountIn.isZero()) throw new Error("amount must be greater than zero");

      const r = raydium.cpmm.computeSwapAmount({
        pool: computePoolInfo,
        amountIn,
        outputMint: outMint,
        slippage,
        swapBaseIn: true,
      });
      setQuote({
        inLabel: `${amount} ${dir === "buy" ? "SOL" : token.symbol}`,
        outLabel: `${fromBaseUnits(r.amountOut, outDecimals)} ${dir === "buy" ? token.symbol : "SOL"}`,
        outRaw: r.amountOut.toString(),
        minOutRaw: r.minAmountOut.toString(),
        priceImpact: r.priceImpact.mul(100).toFixed(4),
        outDecimals,
      });
      say("info", `Quote: ${amountIn.toString()} raw in -> ${r.amountOut.toString()} raw out (${inMint.slice(0, 4)}→${outMint.slice(0, 4)})`);
    } catch (e) {
      say("error", `Quote failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doSwap() {
    if (!wallet || !token?.poolId || !quote) return;
    setBusy(true);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, poolKeys, computePoolInfo } = await raydium.cpmm.getPoolInfoFromRpc(
        token.poolId
      );
      const wsol = NATIVE_MINT.toBase58();
      const baseIn = poolInfo.mintA.address === (dir === "buy" ? wsol : token.mint);
      const outMintStr = dir === "buy" ? token.mint : wsol;
      const inDecimals = dir === "buy" ? 9 : token.decimals;
      const amountIn = toBaseUnits(amount, inDecimals);

      const swapResult = raydium.cpmm.computeSwapAmount({
        pool: computePoolInfo,
        amountIn,
        outputMint: outMintStr,
        slippage,
        swapBaseIn: true,
      });

      const rpcPool = await raydium.cpmm.getRpcPoolInfo(token.poolId, false);
      await waitForPoolOpen(connection, Number(rpcPool.openTime.toString()), (m) => say("info", m));

      const outAta = ataFor(new PublicKey(outMintStr), wallet.publicKey);
      const before = await readTokenBalanceRaw(connection, outAta);

      say("info", "Swap: awaiting wallet approval");
      const { execute } = await raydium.cpmm.swap({
        poolInfo,
        poolKeys,
        inputAmount: amountIn,
        swapResult: swapResult.swapResult,
        slippage,
        baseIn,
        txVersion: TxVersion.V0,
        computeBudgetConfig: {
          units: fees.computeUnitLimit,
          microLamports: fees.computeUnitPriceMicroLamports,
        },
      });
      const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
      await assertTxSucceeded(connection, txId, "Swap");

      const after = await readTokenBalanceRaw(connection, outAta);
      const actual = (after - before).toString();
      setResult({
        signature: txId,
        quotedRaw: swapResult.amountOut.toString(),
        actualRaw: actual,
        decimals: quote.outDecimals,
        symbol: dir === "buy" ? token.symbol : "SOL",
      });
      if (after <= before) {
        throw new Error(
          `swap landed but the output balance did not increase (${before} -> ${after}). ` +
            `Refusing to report this as a successful trade.`
        );
      }
      say("ok", `Swap confirmed: received ${actual} raw`);
      if (actual !== swapResult.amountOut.toString()) {
        say(
          "warn",
          `Quote ${swapResult.amountOut.toString()} != actual ${actual}. The SDK always subtracts the config creator fee even when the pool does not charge it.`
        );
      }
    } catch (e) {
      say("error", `Swap failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <RequireWallet>
      <RequireToken>
        <div className="card">
          <h2>Swap</h2>
          <p className="sub">
            Trades against your own CPMM pool. After the swap lands, the wallet balance delta is
            compared with the SDK's quote — they do not always agree, and it is better to see that
            than to trust the number.
          </p>

          {!token?.poolId ? (
            <div className="empty">No pool loaded for this token.</div>
          ) : (
            <>
              {window_.known && !window_.open ? (
                <Note kind="warn">
                  <p data-testid="pool-not-open">
                    <strong>Trading opens {window_.countdown}.</strong> This pool carries an
                    on-chain open time of {describeOpenTime(window_.openTime as number)}.
                  </p>
                  <p>
                    Until the cluster clock passes it, the cp-swap program rejects every swap with{" "}
                    <code>NotApproved</code> (<code>0x1770</code>) — it is enforced by the program,
                    not by this page, so no other interface can trade it either.
                  </p>
                </Note>
              ) : null}

              <div className="row" style={{ marginBottom: 14 }}>
                <button
                  className={dir === "buy" ? "primary" : ""}
                  onClick={() => {
                    setDir("buy");
                    setQuote(null);
                  }}
                  data-testid="btn-buy"
                >
                  Buy {token.symbol} with SOL
                </button>
                <button
                  className={dir === "sell" ? "primary" : ""}
                  onClick={() => {
                    setDir("sell");
                    setQuote(null);
                  }}
                  data-testid="btn-sell"
                >
                  Sell {token.symbol} for SOL
                </button>
              </div>

              <div className="grid">
                <Field label={`Amount in (${dir === "buy" ? "SOL" : token.symbol})`}>
                  <input
                    data-testid="in-swapamt"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setQuote(null);
                    }}
                  />
                </Field>
                <Field label="Slippage tolerance (%)">
                  <input
                    value={slippagePct}
                    onChange={(e) => setSlippagePct(e.target.value)}
                  />
                </Field>
              </div>

              <div className="row">
                <button onClick={doQuote} disabled={busy} data-testid="btn-quote">
                  Get quote
                </button>
                {/* Disabled rather than left to waitForPoolOpen: that helper exists to
                    cover the seconds between a quote and a landed transaction, not to
                    hang a browser tab for three days waiting for a launch. */}
                <button
                  className="primary"
                  onClick={doSwap}
                  disabled={busy || !quote || (window_.known && !window_.open)}
                  data-testid="btn-swap"
                >
                  {window_.known && !window_.open ? `Opens ${window_.countdown}` : "Swap"}
                </button>
              </div>

              {quote ? (
                <dl className="kv" style={{ marginTop: 16 }} data-testid="quote">
                  <dt>You pay</dt>
                  <dd>{quote.inLabel}</dd>
                  <dt>You receive (quoted)</dt>
                  <dd>{quote.outLabel}</dd>
                  <dt>Minimum after slippage</dt>
                  <dd>{fromBaseUnits(new BN(quote.minOutRaw), quote.outDecimals)}</dd>
                  <dt>Price impact</dt>
                  <dd>{quote.priceImpact}%</dd>
                </dl>
              ) : null}

              {result ? (
                <Note kind={result.quotedRaw === result.actualRaw ? "ok" : "warn"}>
                  <p>
                    Confirmed: <Addr value={result.signature} kind="tx" />
                  </p>
                  <p>
                    Quoted {fromBaseUnits(new BN(result.quotedRaw), result.decimals)} —{" "}
                    <strong data-testid="swap-actual">
                      actually received{" "}
                      {fromBaseUnits(new BN(result.actualRaw), result.decimals)} {result.symbol}
                    </strong>
                  </p>
                  {result.quotedRaw !== result.actualRaw ? (
                    <p>
                      The difference is the configured creator fee, which{" "}
                      <code>computeSwapAmount()</code> always subtracts but the program only
                      charges when <code>enableCreatorFee</code> is set. The error is in the safe
                      direction — you receive at least the quote.
                    </p>
                  ) : null}
                </Note>
              ) : null}
            </>
          )}
        </div>
      </RequireToken>
    </RequireWallet>
  );
}

export function Liquidity() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!wallet || !token?.poolId) return;
    setBusy(true);
    try {
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const { poolInfo, poolKeys } = await raydium.cpmm.getPoolInfoFromRpc(token.poolId);

      if (mode === "add") {
        const baseIn = poolInfo.mintA.address === token.mint;
        const decimals = baseIn ? poolInfo.mintA.decimals : poolInfo.mintB.decimals;
        const inputAmount = toBaseUnits(amount, decimals);
        if (inputAmount.isZero()) throw new Error("amount must be greater than zero");
        say("info", "Add liquidity: awaiting wallet approval");
        const { execute } = await raydium.cpmm.addLiquidity({
          poolInfo,
          poolKeys,
          inputAmount,
          baseIn,
          slippage: new Percent(1, 100),
          txVersion: TxVersion.V0,
          computeBudgetConfig: {
            units: fees.computeUnitLimit,
            microLamports: fees.computeUnitPriceMicroLamports,
          },
        });
        const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
        await assertTxSucceeded(connection, txId, "Add liquidity");
        say("ok", `Liquidity added: ${txId}`);
      } else {
        const lpAmount = new BN(amount.trim());
        if (lpAmount.isZero()) throw new Error("LP amount must be greater than zero");
        say("info", "Remove liquidity: awaiting wallet approval");
        const { execute } = await raydium.cpmm.withdrawLiquidity({
          poolInfo,
          poolKeys,
          lpAmount,
          slippage: new Percent(1, 100),
          txVersion: TxVersion.V0,
          computeBudgetConfig: {
            units: fees.computeUnitLimit,
            microLamports: fees.computeUnitPriceMicroLamports,
          },
        });
        const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
        await assertTxSucceeded(connection, txId, "Remove liquidity");
        say("ok", `Liquidity removed: ${txId}`);
      }
    } catch (e) {
      say("error", `${mode === "add" ? "Add" : "Remove"} liquidity failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <RequireWallet>
      <RequireToken>
        <div className="card">
          <h2>Liquidity</h2>
          <p className="sub">
            Add or withdraw liquidity on your CPMM pool. Adding is priced from one side; the pool
            takes the matching amount of the other at the current ratio.
          </p>
          {!token?.poolId ? (
            <div className="empty">No pool loaded for this token.</div>
          ) : (
            <>
              <div className="row" style={{ marginBottom: 14 }}>
                <button className={mode === "add" ? "primary" : ""} onClick={() => setMode("add")}>
                  Add
                </button>
                <button
                  className={mode === "remove" ? "primary" : ""}
                  onClick={() => setMode("remove")}
                >
                  Remove
                </button>
              </div>
              <Field
                label={mode === "add" ? `Amount of ${token.symbol} to add` : "LP tokens to burn (raw units)"}
                hint={
                  mode === "add"
                    ? "The equivalent SOL is pulled from your wallet automatically."
                    : "Raw LP units — see 'Your LP tokens' on the Dashboard."
                }
              >
                <input
                  data-testid="in-liq"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <button
                className="primary"
                onClick={submit}
                disabled={busy || !amount.trim()}
                data-testid="btn-liq"
              >
                {busy ? "Working…" : mode === "add" ? "Add liquidity" : "Remove liquidity"}
              </button>
            </>
          )}
        </div>
      </RequireToken>
    </RequireWallet>
  );
}
